import React, { useEffect, useState } from "react";
import { FilterDate } from "../../../types/commonForm";
import { WowcherCoupon } from "../../../types/coupons";
import { useDataProvider, useNotify } from "react-admin";
import { Box, Dialog, DialogContent } from "@material-ui/core";
import { AppButton } from "../../common/ctaButton/button";
import { RemoveEntityModalContent } from "../../common/removeEntityPopUp/RemoveEntity";
import { CouponsWowcherTable } from "../../common/couponsShared/couponsWowcherTable/couponsWowcherTable";
import { CouponWowcherCreateModal } from "../../common/couponsShared/couponsModals/couponWowcherCreateModal";
import { CouponWowcherViewModal } from "../../common/couponsShared/couponsModals/couponWowcherModalView";

interface State {
  coupons: Array<WowcherCoupon>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
  statusFilter: Array<string>;
}

export const WowcherCouponsTab = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [state, setState] = useState<State>({
    coupons: Array<WowcherCoupon>(),
    total: 0,
    page: 0,
    perPage: 10,
    field: "_id",
    order: "desc",
    searchField: "couponId",
    searchValue: "",
    statusFilter: Array<string>(),
  });

  const [currentCoupon, setCurrentCoupon] = useState<WowcherCoupon>();
  const [modalsState, setModals] = useState({
    viewCoupon: false,
    createCoupon: false,
    deleteCoupon: false,
  });

  const [statusFilter, setStatusFilter] = useState({
    Active: false,
    Used: false,
  });

  const handleShowModals = (
    modalName: "viewCoupon" | "createCoupon" | "deleteCoupon"
  ): (() => void) => {
    if (modalName === "viewCoupon" || modalName === "deleteCoupon") {
      return (couponId?: string) => {
        const coupon = state.coupons.find((i) => i._id === couponId);
        setCurrentCoupon(coupon);
        setModals((state) => ({
          ...state,
          [modalName]: true,
        }));
      };
    }
    return () => {
      setModals((state) => ({
        ...state,
        [modalName]: true,
      }));
    };
  };

  const handleCloseModals =
    (modalName: "viewCoupon" | "createCoupon" | "deleteCoupon") => () => {
      setModals((state) => ({
        ...state,
        [modalName]: false,
      }));
    };

  useEffect(() => {
    getWowcherCoupons(
      1,
      10,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      Array<string>(),
    );
  }, []);

  const getWowcherCoupons = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchField: string,
    searchValue: string,
    statusFilter: Array<string>,
  ): Promise<void> => {
    const result = (await dataProvider.getCoupons(
      "/coupons/get/referral",
      {
        pagination: { page, perPage },
        sort: { field, order },
        search: { field: searchField, value: searchValue },
        filter: {
          statusFilter: statusFilter,
        },
      }
    )) as any;
    setState({
      ...state,
      coupons: result.data ? result.data.coupons : [],
      total: result.data ? result.data.allCount : 0,
      page,
      perPage,
      field,
      order,
      searchField,
      searchValue,
      statusFilter,
    });
  };

  const handleDeleteCoupon = (couponId: string) => async () => {
    try {
      const result = await dataProvider.delete("coupons/delete/referral", {
        id: couponId,
        previousData: {} as any,
      });
      if (result.data) {
        notify(result.data.message, "info");
        getWowcherCoupons(
          state.page,
          state.perPage,
          state.field,
          state.order,
          state.searchField,
          state.searchValue,
          state.statusFilter,
        );
        setModals({
          ...modalsState,
          deleteCoupon: false,
        });
      }
    } catch (error) {
      notify("Coupon not found", "error");
    }
  };

  const handleFirstPage = () => {
    getWowcherCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getWowcherCoupons(
      page,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getWowcherCoupons(
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getWowcherCoupons(
      1,
      perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
    );
  };

  const handleSort = (direction: string, field: string) => {
    getWowcherCoupons(
      state.page,
      state.perPage,
      field,
      direction,
      state.searchField,
      state.searchValue,
      state.statusFilter,
    );
  };

  const applySearch = (field: string) => {
    getWowcherCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      field,
      state.searchValue,
      state.statusFilter,
    );
  };

  const checkStatusFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const filter = statusFilter;
    filter[name] = event.target.checked;
    setState({
      ...state,
      statusFilter: Object.keys(filter).filter((status) => filter[status]),
    });
    setStatusFilter({ ...statusFilter, [name]: event.target.checked });
  };

  const clearStatusFilter = () => {
    setStatusFilter({
      ...statusFilter,
      Active: false,
      Used: false,
    });
    setState({ ...state, statusFilter: Array<string>() });
  };

  const search = (value: string, field: string) => {
    setState({ ...state, searchField: field, searchValue: value });
  };

  const clearSearch = () => {
    setState({ ...state, searchValue: "" });
  };

  const applyStatusFilter = () => {
    const filter = Object.keys(statusFilter).filter(
      (status) => statusFilter[status]
    );
    getWowcherCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      filter,
    );
  };

  return (
    <div>
      {" "}
      <Box className="coupons">
        <div className="coupons__actions">
          <AppButton
            onClick={handleShowModals("createCoupon")}
            text="Add Wowcher Coupon"
          />
        </div>
        <CouponsWowcherTable
          coupons={state.coupons}
          page={state.page}
          perPage={state.perPage}
          collectionLength={state.coupons.length}
          total={state.total}
          isView={false}
          handleShowViewModal={handleShowModals("viewCoupon")}
          handleShowDeleteModal={handleShowModals("deleteCoupon")}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleFirstPage={handleFirstPage}
          handleLastPage={handleLastPage}
          handleSort={handleSort}
          applySearch={applySearch}
          statusFilterValues={statusFilter}
          checkStatusFilter={checkStatusFilter}
          clearStatusFilter={clearStatusFilter}
          search={search}
          clearSearch={clearSearch}
          searchValue={state.searchValue}
          applyStatusFilter={applyStatusFilter}
        />
        {modalsState.viewCoupon && currentCoupon && (
          <CouponWowcherViewModal
            coupon={currentCoupon}
            closeModal={handleCloseModals("viewCoupon")}
            showModal={modalsState.viewCoupon}
          />
        )}
        {modalsState.createCoupon && (
          <CouponWowcherCreateModal
            closeModal={handleCloseModals("createCoupon")}
            showModal={modalsState.createCoupon}
            refresh={() =>
              getWowcherCoupons(
                state.page,
                state.perPage,
                state.field,
                state.order,
                state.searchField,
                state.searchValue,
                state.statusFilter,
              )
            }
          />
        )}
        {modalsState.deleteCoupon && currentCoupon && (
          <Dialog
            fullWidth
            open={modalsState.deleteCoupon}
            onClose={handleCloseModals("deleteCoupon")}
          >
            <DialogContent>
              <RemoveEntityModalContent
                closeModal={handleCloseModals("deleteCoupon")}
                removeUser={handleDeleteCoupon(currentCoupon?._id!)}
                entityName="wowcher coupon"
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </div>
  );
};
